import { buildApiUrl } from "@/helpers/apiUrl";

export interface AuthLoginRequestResponse {
  sent?: boolean;
  email?: string;
  emailKey?: string;
  message?: string;
  error?: string;
  locked?: boolean;
  retry_after_seconds?: number;
  debug_pincode?: string;
}

export interface AuthLoginVerifyResponse {
  verified?: boolean;
  customToken?: string;
  email?: string;
  emailKey?: string;
  message?: string;
  locked?: boolean;
  retry_after_seconds?: number;
  remaining_attempts?: number;
  error?: string;
}

let apiWarmed = false;

// The API scales to zero when idle and takes several seconds to start. Asking
// for its health check while the person is still typing their email starts it
// early, so sending the code does not wait on that start-up. A plain GET needs
// no CORS preflight; the reply is not read.
export function warmAuthVerifyApi(): void {
  if (apiWarmed) {
    return;
  }
  const url = buildApiUrl("health");
  if (!url || typeof fetch !== "function") {
    return;
  }
  apiWarmed = true;
  fetch(url, { mode: "no-cors", credentials: "omit", cache: "no-store" }).catch(
    () => undefined
  );
}

async function postJson<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<{ status: number; data: T }> {
  const url = buildApiUrl(path);
  if (!url) {
    throw new Error("VUE_APP_API_URL is not configured");
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });

  let data = {} as T;
  try {
    data = (await response.json()) as T;
  } catch {
    throw new Error("Verification service returned an invalid response");
  }
  if (response.status >= 500) {
    throw new Error("Verification service is temporarily unavailable");
  }

  return { status: response.status, data };
}

export async function postAuthLoginRequest(payload: {
  email: string;
  hostname?: string;
}): Promise<{ status: number; data: AuthLoginRequestResponse }> {
  return postJson<AuthLoginRequestResponse>("auth/login/request", payload);
}

export async function postAuthLoginVerify(payload: {
  email: string;
  pincode: string;
}): Promise<{ status: number; data: AuthLoginVerifyResponse }> {
  return postJson<AuthLoginVerifyResponse>("auth/login/verify", payload);
}

export type AuthSignupRequestResponse = AuthLoginRequestResponse;

export type AuthSignupVerifyResponse = Omit<
  AuthLoginVerifyResponse,
  "customToken"
>;

export async function postAuthSignupRequest(payload: {
  email: string;
  hostname?: string;
  lang?: string;
}): Promise<{ status: number; data: AuthSignupRequestResponse }> {
  return postJson<AuthSignupRequestResponse>("auth/signup/request", payload);
}

export async function postAuthSignupVerify(payload: {
  email: string;
  pincode: string;
}): Promise<{ status: number; data: AuthSignupVerifyResponse }> {
  return postJson<AuthSignupVerifyResponse>("auth/signup/verify", payload);
}

/** Guest → account email: the PIN must have been requested by this guest. */
export async function postAuthGuestVerify(payload: {
  email: string;
  pincode: string;
  guestEmail: string;
}): Promise<{ status: number; data: AuthSignupVerifyResponse }> {
  return postJson<AuthSignupVerifyResponse>("auth/guest/verify", payload);
}
